import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import SearchBox from '../../../ui/SearchBox';
import useFilter from '../useFilter';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchTerm } from '../../../../redux/slices/filterSlice';
import { searchClient } from '../../../../utils/config';
import { debounce } from 'lodash';
import { useLocation, useHistory } from 'react-router-dom';

const FilterSearchBox = ({ className = '' }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const searchBoxRef = useRef(null);
  const { searchTerm, handleSearchChange } = useFilter();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const reduxSearchTerm = useSelector(state => state.filter.searchTerm);

  // Initialize suggestions index - memoize to prevent recreation
  const productsIndex = useMemo(() => 
    searchClient.initIndex(process.env.REACT_APP_ALGOLIA_PRODUCTS_INDEX || 'products')
  , []);

  // Update dropdown position when suggestions change or on scroll/resize
  useEffect(() => {
    const updatePosition = () => {
      if (searchBoxRef.current) {
        const rect = searchBoxRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updatePosition();

    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [suggestions]);

  // Sync query with searchTerm from Redux
  useEffect(() => {
    setQuery(reduxSearchTerm || '');
  }, [reduxSearchTerm]);

  // Debounced function for suggestions
  const debouncedSearch = useRef(
    debounce(async (searchQuery) => {
      if (!searchQuery.trim()) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const { hits } = await productsIndex.search(searchQuery, {
          hitsPerPage: 5,
          attributesToRetrieve: ['name'],
          attributesToHighlight: ['name']
        });
        
        // Extract unique names for suggestions
        const uniqueSuggestions = [...new Set(hits.map(hit => hit.name))];
        setSuggestions(uniqueSuggestions.map(name => ({
          objectID: name,
          query: name
        })));
      } catch (error) {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 200)
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle input change - only update local state and get suggestions
  const handleInputChange = (value) => {
    setQuery(value);
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      setSuggestions([]);
    }
  };

  // Handle search submission - update URL and trigger search
  const handleSearch = (value) => {
    // Use handleSearchChange from useFilter instead of directly updating URL
    // This will ensure other filters are preserved
    handleSearchChange(value);
    
    // Clear suggestions
    setSuggestions([]);
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Render suggestions in a portal
  const renderSuggestions = () => {
    if (!suggestions.length || !query) return null;

    return createPortal(
      <div 
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 9999,
        }}
        className="bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
      >
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.objectID}
            className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100 text-gray-700"
            onMouseDown={() => {
              handleSearch(suggestion.query);
            }}
          >
            {suggestion.query}
          </div>
        ))}
      </div>,
      document.body
    );
  };

  return (
    <div ref={searchBoxRef} className="relative">
      <SearchBox
        title="Search"
        value={query}
        onChange={handleInputChange}
        onSearch={handleSearch}
        placeholder="Search products..."
        maxWidth="none"
        className={`text-gray-500 ${className}`}
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-12 top-1/2 transform -translate-y-1/2 z-50">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-teal"></div>
        </div>
      )}

      {/* Render suggestions through portal */}
      {renderSuggestions()}
    </div>
  );
};

export default FilterSearchBox; 